import React, { useState, useEffect } from 'react';
import { ListTodo, User, Globe, Clock, Filter, Search, RefreshCw, Shield } from 'lucide-react';

const ActivityLog = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ user: 'all', type: 'all', dateRange: '7d' });
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({ total: 0, unique_actors: 0, last_24h: 0, admin_actions: 0, actors: [] });

  useEffect(() => {
    const t = setTimeout(fetchActivityLogs, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, searchTerm]);

  const fetchActivityLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('gio_token') || localStorage.getItem('token');
      
      // Build query params
      const params = new URLSearchParams();
      if (filter.user !== 'all') params.append('actor_filter', filter.user);
      if (filter.type !== 'all') params.append('type_filter', filter.type);
      if (searchTerm.trim()) params.append('search', searchTerm.trim());
      params.append('date_range', filter.dateRange);
      params.append('limit', '100');
      
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/activity-logs?${params}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      if (res.ok) {
        const data = await res.json();
        setLogs(data.logs || []);
        setStats({
          total: data.total || 0,
          unique_actors: data.unique_actors || 0,
          last_24h: data.last_24h || 0,
          admin_actions: data.admin_actions || 0,
          actors: data.actors || [],
        });
      } else {
        console.error('Failed to fetch activity logs');
      }
    } catch (err) {
      console.error('Failed to fetch activity logs:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = logs;

  const getActivityIcon = (log) => {
    if (log.endpoint.includes('/admin')) return <Shield size={16} className="text-purple-400" />;
    if (log.method === 'POST' || log.method === 'PUT') return <RefreshCw size={16} className="text-blue-400" />;
    return <Globe size={16} className="text-zinc-400" />;
  };

  const getMethodBadge = (method) => {
    const colors = {
      'GET': 'bg-blue-500/10 text-blue-400 border-blue-500/20',
      'POST': 'bg-green-500/10 text-green-400 border-green-500/20',
      'PUT': 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
      'DELETE': 'bg-red-500/10 text-red-400 border-red-500/20'
    };
    return (
      <span className={`inline-block px-2 py-0.5 text-[10px] font-mono font-semibold border rounded ${colors[method] || 'bg-zinc-800 text-zinc-400'}`}>
        {method}
      </span>
    );
  };

  if (loading && logs.length === 0) {
    return (
      <div className="min-h-screen bg-black text-white p-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <ListTodo size={32} className="text-purple-400" />
            <div>
              <h1 className="text-3xl font-bold">Activity Log</h1>
              <p className="text-zinc-400 text-sm">Loading activity data...</p>
            </div>
          </div>
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <ListTodo size={32} className="text-purple-400" />
            <div>
              <h1 className="text-3xl font-bold">Activity Log</h1>
              <p className="text-zinc-400 text-sm">Admin actions & system events (Admin Only)</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="px-3 py-1.5 bg-purple-500/10 border border-purple-500/20 rounded-lg text-sm font-medium text-purple-400">
              Live
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="mb-6 p-4 bg-zinc-900 rounded-xl border border-zinc-800">
          <div className="flex items-center gap-2 mb-3">
            <Filter size={16} className="text-zinc-400" />
            <span className="text-sm font-medium text-zinc-400">Filters</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs text-zinc-400 mb-1">User</label>
              <select 
                value={filter.user}
                onChange={(e) => setFilter({ ...filter, user: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white focus:outline-none focus:border-purple-500"
              >
                <option value="all">All Users</option>
                {[...new Set([...(filter.user !== 'all' ? [filter.user] : []), ...stats.actors])].map(a => (
                  <option key={a} value={a}>{a}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Type</label>
              <select 
                value={filter.type}
                onChange={(e) => setFilter({ ...filter, type: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white focus:outline-none focus:border-purple-500"
              >
                <option value="all">All Actions</option>
                <option value="admin">Admin Actions</option>
                <option value="platform">Platform Actions</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Date Range</label>
              <select 
                value={filter.dateRange}
                onChange={(e) => setFilter({ ...filter, dateRange: e.target.value })}
                className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white focus:outline-none focus:border-purple-500"
              >
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="all">All Time</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-zinc-400 mb-1">Search</label>
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Action or endpoint..."
                  className="w-full pl-9 pr-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <ListTodo size={16} className="text-purple-400" />
              <span className="text-xs text-zinc-400 uppercase tracking-wide">Total Actions</span>
            </div>
            <p className="text-2xl font-bold">{stats.total}</p>
          </div>
          <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <User size={16} className="text-blue-400" />
              <span className="text-xs text-zinc-400 uppercase tracking-wide">Unique Users</span>
            </div>
            <p className="text-2xl font-bold">{stats.unique_actors}</p>
          </div>
          <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Clock size={16} className="text-green-400" />
              <span className="text-xs text-zinc-400 uppercase tracking-wide">Last 24h</span>
            </div>
            <p className="text-2xl font-bold">{stats.last_24h}</p>
          </div>
          <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <Shield size={16} className="text-purple-400" />
              <span className="text-xs text-zinc-400 uppercase tracking-wide">Admin Actions</span>
            </div>
            <p className="text-2xl font-bold">{stats.admin_actions}</p>
          </div>
        </div>

        {/* Activity Timeline */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-zinc-800">
            <h2 className="text-lg font-semibold">Activity Timeline</h2>
            <p className="text-sm text-zinc-400 mt-1">
              Showing {filteredLogs.length} of {stats.total} actions
            </p>
          </div>
          
          {filteredLogs.length === 0 ? (
            <div className="p-8 text-center text-zinc-500">
              <ListTodo size={48} className="mx-auto mb-3 opacity-50" />
              <p>No activity logs found matching filters</p>
            </div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {filteredLogs.map((log, i) => (
                <div key={log.id || `${log.timestamp}-${i}`} className="p-4 hover:bg-zinc-800/50 transition">
                  <div className="flex items-start gap-3">
                    <div className="mt-1">{getActivityIcon(log)}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-white">{log.action || `${log.method} ${log.endpoint}`}</p>
                        {getMethodBadge(log.method)}
                      </div>
                      <p className="text-sm text-zinc-400 font-mono mb-2">{log.endpoint}</p>
                      <div className="flex items-center gap-4 text-xs text-zinc-500">
                        <span className="flex items-center gap-1">
                          <User size={12} />
                          {log.user || log.actor}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {new Date(log.timestamp).toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Globe size={12} />
                          {log.ip}
                        </span>
                        {log.user_agent && (
                          <span className="truncate max-w-xs" title={log.user_agent}>{log.user_agent}</span>
                        )}
                        {log.status_code >= 400 && (
                          <span className="text-red-400">HTTP {log.status_code}</span>
                        )}
                        {log.job_status && (
                          <span className={log.job_status === 'completed' ? 'text-green-400' : log.job_status === 'skipped' ? 'text-yellow-400' : 'text-red-400'}>
                            {log.job_status}
                          </span>
                        )}
                        {log.processed_count !== undefined && <span>{log.processed_count} processed</span>}
                        {log.reason && <span className="text-zinc-400">{log.reason}</span>}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ActivityLog;
